import React from 'react'
import { useRouter } from 'next/router'
import { SrOnly } from 'pcln-design-system'
import {
  GetItemPropsOptions,
  GetMenuPropsOptions,
  GetPropsCommonOptions
} from 'downshift'
import INPUT_MIN_WIDTH from '@/constants/dropdown'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import analytics from '@/shared-utils/analytics'
import { LOCATION_SEARCH_TYPE, TypeAheadConfigEndpoint } from '@/types'
import DropdownCard from './DropdownCard'
import FetchTypeAhead from '../FetchTypeAhead'
import SearchItem from './SearchItem'
import RenderCurrentLocation from './RenderCurrentLocation'
import RenderRecentSearches from './RenderRecentSearches'
import { CurrentLocationMachineState } from '../../../machines/currentLocationMachine'

type DropdownProps = {
  isOpen: boolean
  highlightedIndex: number | null
  getItemProps: (options: GetItemPropsOptions<unknown>) => any
  getMenuProps: (
    options?: GetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions
  ) => any
  searchKey: string
  searchValue: string | null
  clearItems: () => void
  setHighlightedIndex: (index: number) => void
  showCurrentLocation?: boolean
  isMobile?: boolean
  current?: CurrentLocationMachineState
  handleRequestCurrentLocationClick?: () => void
  typeAheadEndpoint: TypeAheadConfigEndpoint
  recentSearches?: LOCATION_SEARCH_TYPE[]
  label?: string
  recentOrRecommendationTitle: string
}

function Dropdown({
  isOpen,
  highlightedIndex,
  getItemProps,
  getMenuProps,
  searchKey,
  searchValue,
  clearItems,
  current,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleRequestCurrentLocationClick = () => {},
  showCurrentLocation = false,
  setHighlightedIndex,
  isMobile = false,
  typeAheadEndpoint,
  recentSearches = [],
  label = '',
  recentOrRecommendationTitle
}: DropdownProps) {
  const { pathname } = useRouter()
  const isHomepage = pathname === '/'
  const isFlightOrHotelTab =
    typeAheadEndpoint === 'flights' || typeAheadEndpoint === 'hotels'
  const gaLabel = label.includes('to') ? 'going to' : 'departing'

  return isOpen ? (
    <FetchTypeAhead
      searchValue={searchValue}
      searchKey={searchKey}
      typeAheadEndpoint={typeAheadEndpoint}
      onLoaded={() => {
        clearItems()
        setHighlightedIndex(0)
      }}
      render={({ items }: { items: LOCATION_SEARCH_TYPE[] }) => {
        const isCardVisible = isOpen && items.length
        const isRecentSearchesVisible =
          isOpen && recentSearches.length && searchValue === ''
        return (
          <>
            {isCardVisible > 0 && (
              <SrOnly aria-live="polite">
                {items.length} number of results
              </SrOnly>
            )}
            <DropdownCard
              boxShadowSize={!isMobile && 'overlay-lg'}
              data-testid="typeahead-dropdown-card"
              color="background.lightest"
              borderRadius="xl"
              p={isMobile ? 0 : 2}
              mt={isMobile ? 0 : 1}
              pr={isMobile ? 4 : undefined}
              role="listbox"
              rounded={isMobile ? 'bottom' : ''}
              width={1}
              minWidth={isMobile ? '' : INPUT_MIN_WIDTH}
              visible={
                isCardVisible || isRecentSearchesVisible || showCurrentLocation
              }
              {...getMenuProps({
                refKey: 'ref',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                isOpen
              })}
            >
              {showCurrentLocation && current && (
                <RenderCurrentLocation
                  current={current}
                  handleRequestCurrentLocationClick={
                    handleRequestCurrentLocationClick
                  }
                />
              )}
              {isRecentSearchesVisible ? (
                <RenderRecentSearches
                  isMobile={isMobile}
                  getItemProps={getItemProps}
                  recentSearches={recentSearches}
                  highlightedIndex={highlightedIndex}
                  recentOrRecommendationTitle={recentOrRecommendationTitle}
                  gaTracking={() => {
                    return isHomepage && isFlightOrHotelTab
                      ? analytics.fireEvent({
                          category: ANALYTICS_CATEGORY_HOME,
                          action: `${typeAheadEndpoint} ${recentOrRecommendationTitle.toLowerCase()}`,
                          label: gaLabel
                        })
                      : undefined
                  }}
                />
              ) : null}
              {isCardVisible
                ? items.map((item, index) => (
                    <SearchItem
                      isMobile={isMobile}
                      key={item.itemName}
                      isItemSelected={index === highlightedIndex}
                      index={index}
                      item={item}
                      getItemProps={getItemProps}
                    />
                  ))
                : null}
            </DropdownCard>
          </>
        )
      }}
    />
  ) : null
}

export default Dropdown
